import { Link } from "@tanstack/react-router";
import { useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { FadeImg } from "@/components/fade-img";

interface Product {
  id: string;
  name: string;
  price?: number;
  priceLabel?: string;
  status: string;
  emoji?: string;
  tag?: string;
  imageUrl?: string;
}

function cloudImg(url: string, width = 500) {
  if (!url || !url.includes("cloudinary.com")) return url;
  return url.replace("/upload/", `/upload/w_${width},f_auto,q_auto/`);
}

export function ProductCarousel({ title, products }: { title: string; products: Product[] }) {
  const scrollerRef = useRef<HTMLDivElement>(null);

  function scroll(dir: 1 | -1) {
    scrollerRef.current?.scrollBy({ left: dir * 280, behavior: "smooth" });
  }

  if (products.length === 0) return null;

  return (
    <section className="border-t border-border py-16 sm:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <h2 className="mb-10 text-center text-2xl uppercase tracking-[-0.01em] sm:text-3xl">{title}</h2>

        <div className="relative">
          <button
            onClick={() => scroll(-1)}
            className="absolute -left-2 top-[35%] z-10 hidden h-9 w-9 -translate-y-1/2 items-center justify-center border border-border bg-background text-foreground transition-colors hover:border-foreground sm:flex"
            aria-label="Scroll left"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          <div
            ref={scrollerRef}
            className="flex gap-4 overflow-x-auto scroll-smooth pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {products.map((p, i) => {
              const sold = p.status === "sold";
              return (
                <Link
                  key={p.id + i}
                  to="/product/$id"
                  params={{ id: p.id }}
                  className={`group w-[200px] flex-shrink-0 transition-transform duration-300 ease-out ${sold ? "opacity-50" : "hover:-translate-y-1"}`}
                >
                  <div className="relative aspect-square overflow-hidden bg-card">
                    {p.imageUrl ? (
                      <FadeImg
                        src={cloudImg(p.imageUrl, 500)}
                        alt={p.name}
                        className={`h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.05] ${sold ? "grayscale" : ""}`}
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-5xl">{p.emoji}</div>
                    )}
                    {sold && (
                      <span className="absolute right-2 top-2 bg-foreground px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] text-background">
                        Sold Out
                      </span>
                    )}
                  </div>
                  <div className="pt-3 text-center">
                    <p className="line-clamp-1 text-[11px] uppercase tracking-[0.05em] text-foreground">{p.name}</p>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      {p.price ? `LE ${p.price}` : p.priceLabel || "DM for price"}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>

          <button
            onClick={() => scroll(1)}
            className="absolute -right-2 top-[35%] z-10 hidden h-9 w-9 -translate-y-1/2 items-center justify-center border border-border bg-background text-foreground transition-colors hover:border-foreground sm:flex"
            aria-label="Scroll right"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
